import { useEffect, useState } from 'react';
import { TableProps } from '@/theme/components/Table/Table';
import { ColumnValue, Icons, Table, Text, Stack, styled } from '@/theme/index';
import { commaFormatCurrency } from '@/utils/commaFormat/commaFormatCurrency';
import {
  ChartFormattedData,
  PartnersVisualizationProps,
  PartnersProps,
} from '../EstimatedValue';

const StyledTable = styled(Table)(({ theme }) => ({
  thead: {
    th: {
      backgroundColor: theme.palette.primary.main,
      color:
        theme.palette.customCard?.light ?? theme.palette.primary.contrastText,
      fontSize: theme.spacing(2),
      fontWeight: '700',
    },
  },

  tbody: {
    tr: {
      td: {
        fontSize: theme.spacing(2),
        padding: theme.spacing(2),
      },
    },
    td: {
      position: 'relative',
    },
  },
}));

const StylesForTableArrow = {
  width: '20px',
  height: '20px',
  position: 'absolute',
};

function formatTableHeaders(avmsPartners: PartnersProps[]) {
  const tableHeaders =
    avmsPartners?.map((partnerObject: PartnersProps) => partnerObject.name) ||
    [];
  return ['Month', ...tableHeaders];
}

function getTableNodeWithDirection(item?: ChartFormattedData | undefined) {
  const textElement = item?.value ? commaFormatCurrency(item?.value) : '-';
  const textNode = (
    <Text variant="body1" ml="25px" fontSize="14px">
      {textElement}
    </Text>
  );

  if (textElement !== '-' && item?.direction) {
    let tableNode;

    if (item.direction === 'up') {
      tableNode = (
        <Stack direction="row">
          <Icons.ArrowUpward color="primary" sx={StylesForTableArrow} />
          {textNode}
        </Stack>
      );
    } else {
      tableNode = (
        <Stack direction="row">
          <Icons.ArrowDownward color="error" sx={StylesForTableArrow} />
          {textNode}
        </Stack>
      );
    }
    return tableNode;
  }

  return textNode;
}

function formatDataForTable(
  avmsPartners: PartnersProps[],
  categories: string[]
) {
  const formattedRows = avmsPartners.reduce(
    (result: ColumnValue[][], partnerObject: PartnersProps) => {
      for (let index = 0; index < categories?.length; index++) {
        const activeAVM = partnerObject?.data?.find(
          (item: ChartFormattedData) => {
            const { month, year } = item.date?.parsed || {};
            return `${month}/${(year || 0) % 2000}` === categories[index];
          }
        );

        if (!result[index]) {
          result[index] = [categories[index]];
        }

        if (activeAVM) {
          result[index].push(getTableNodeWithDirection(activeAVM));
        } else {
          result[index].push(getTableNodeWithDirection());
        }
      }

      return result;
    },
    []
  );

  return formattedRows;
}

export function MarketValueTable({
  data,
  categories,
}: PartnersVisualizationProps) {
  const [tableData, setData] = useState<TableProps>();

  useEffect(() => {
    if (data?.length) {
      const tableData = formatDataForTable(data, categories || []);

      const formattedData = {
        headers: formatTableHeaders(data),
        rows: tableData,
      };

      setData(formattedData);
    }
  }, [data]);

  return (
    <Stack width="100%" mt={2.5} sx={{ overflowX: 'scroll' }}>
      {tableData && (
        <StyledTable
          rows={tableData?.rows}
          className="striped"
          headers={tableData?.headers}
        />
      )}
    </Stack>
  );
}
